import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";

const SpecialBusinessHoursDialog = ({
  open,
  onClose,
  onSave,
  item,
  loading = false,
  lang,
}) => {
  const isFrench = lang === "FR";
  const isEdit = Boolean(item);

  const [date, setDate] = useState("");
  const [opening, setOpening] = useState("");
  const [closing, setClosing] = useState("");

  // NEW
  const [closedAllDay, setClosedAllDay] = useState(false);

  const [error, setError] = useState("");

  // ============================================================
  // Initialize form
  // ============================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setError("");

    if (item) {
      const itemOpening = item.opening || "";
      const itemClosing = item.closing || "";

      setDate(formatDateForInput(item.date));

      // Detect a full-day closure
      const isClosed = itemOpening === "00:00" && itemClosing === "12:00";

      setClosedAllDay(isClosed);

      setOpening(itemOpening);
      setClosing(itemClosing);
    } else {
      setDate("");
      setOpening("");
      setClosing("");
      setClosedAllDay(false);
    }
  }, [open, item]);

  // ============================================================
  // Helpers
  // ============================================================

  const formatDateForInput = (value) => {
    if (!value) {
      return "";
    }

    return String(value).substring(0, 10);
  };

  const getToday = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ============================================================
  // Closed all day
  // ============================================================

  const handleClosedAllDayChange = (event) => {
    const checked = event.target.checked;

    setClosedAllDay(checked);
    setError("");

    if (checked) {
      // Full day closure
      setOpening("00:00");
      setClosing("23:59");
    } else {
      // Clear values when reopening the time fields
      setOpening("");
      setClosing("");
    }
  };

  // ============================================================
  // Validation
  // ============================================================

  const validate = () => {
    if (!date) {
      setError(
        isFrench ? "Veuillez sélectionner une date." : "Please select a date.",
      );

      return false;
    }

    // Current and future dates only
    if (date < getToday()) {
      setError(
        isFrench
          ? "La date doit être aujourd'hui ou une date future."
          : "The date must be today or a date in the future.",
      );

      return false;
    }

    // If the entire day is closed,
    // opening and closing times don't need normal validation.
    if (closedAllDay) {
      return true;
    }

    if (!opening) {
      setError(
        isFrench
          ? "Veuillez sélectionner une heure d'ouverture."
          : "Please select an opening time.",
      );

      return false;
    }

    if (!closing) {
      setError(
        isFrench
          ? "Veuillez sélectionner une heure de fermeture."
          : "Please select a closing time.",
      );

      return false;
    }

    // Opening must be before closing
    if (opening >= closing) {
      setError(
        isFrench
          ? "L'heure d'ouverture doit être avant l'heure de fermeture."
          : "The opening time must be before the closing time.",
      );

      return false;
    }

    return true;
  };

  // ============================================================
  // Save
  // ============================================================

  const handleSubmit = async () => {
    setError("");

    if (!validate()) {
      return;
    }

    const payload = {
      date,

      // If closed all day:
      // 00:00 -> 12:00
      opening: closedAllDay ? "00:00" : opening,
      closing: closedAllDay ? "23:59" : closing,
    };

    try {
      await onSave(payload);
    } catch (err) {
      console.error("Error saving special business hours:", err);

      setError(
        isFrench
          ? err?.messageFr ||
              "Une erreur est survenue lors de l'enregistrement."
          : err?.messageEn ||
              "An error occurred while saving the special business hours.",
      );
    }
  };

  // ============================================================
  // Close
  // ============================================================

  const handleClose = () => {
    if (loading) {
      return;
    }

    setError("");
    onClose();
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                color: "#fff",
              }}
            >
              {isEdit ? (
                <AccessTimeRoundedIcon />
              ) : (
                <EventAvailableRoundedIcon />
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isEdit
                  ? isFrench
                    ? "Modifier les heures spéciales"
                    : "Edit Special Business Hours"
                  : isFrench
                    ? "Ajouter des heures spéciales"
                    : "Add Special Business Hours"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {isEdit
                  ? isFrench
                    ? "Modifiez l'horaire exceptionnel de cette date."
                    : "Update the exceptional hours for this date."
                  : isFrench
                    ? "Configurez une ouverture exceptionnelle pour une date."
                    : "Configure exceptional opening hours for a date."}
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            aria-label={isFrench ? "Fermer" : "Close"}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={3}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* DATE */}

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <CalendarMonthRoundedIcon fontSize="small" color="primary" />

              <Typography fontWeight={600}>
                {isFrench ? "Date" : "Date"}
              </Typography>
            </Stack>

            <TextField
              fullWidth
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setError("");
              }}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: getToday(),
              }}
              helperText={
                isFrench
                  ? "Seules les dates courantes et futures sont autorisées."
                  : "Only current and future dates are allowed."
              }
            />
          </Box>

          {/* ==================================================
              CLOSED ALL DAY
          ================================================== */}

          <Box
            sx={{
              border: "1px solid",
              borderColor: closedAllDay ? "error.main" : "divider",
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: closedAllDay ? "error.50" : "background.paper",
              transition: "all 0.2s ease",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={closedAllDay}
                  onChange={handleClosedAllDayChange}
                  disabled={loading}
                  color="error"
                />
              }
              label={
                <Box>
                  <Typography
                    fontWeight={700}
                    color={closedAllDay ? "error.main" : "text.primary"}
                  >
                    {isFrench ? "Fermé toute la journée" : "Closed all day"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {isFrench
                      ? "Le commerce sera fermé pendant toute cette journée."
                      : "The location will be closed for the entire day."}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* OPENING / CLOSING */}

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
          >
            {/* OPENING */}

            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <AccessTimeRoundedIcon
                  fontSize="small"
                  color={closedAllDay ? "disabled" : "success"}
                />

                <Typography
                  fontWeight={600}
                  color={closedAllDay ? "text.disabled" : "text.primary"}
                >
                  {isFrench ? "Ouverture" : "Opening"}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                type="time"
                value={opening}
                onChange={(e) => {
                  setOpening(e.target.value);
                  setError("");
                }}
                disabled={loading || closedAllDay}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60,
                }}
              />
            </Box>

            {/* CLOSING */}

            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <AccessTimeRoundedIcon
                  fontSize="small"
                  color={closedAllDay ? "disabled" : "error"}
                />

                <Typography
                  fontWeight={600}
                  color={closedAllDay ? "text.disabled" : "text.primary"}
                >
                  {isFrench ? "Fermeture" : "Closing"}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                type="time"
                value={closing}
                onChange={(e) => {
                  setClosing(e.target.value);
                  setError("");
                }}
                disabled={loading || closedAllDay}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60,
                }}
              />
            </Box>
          </Stack>

          {/* INFORMATION */}

          <Alert
            severity={closedAllDay ? "warning" : "info"}
            icon={<EventAvailableRoundedIcon />}
            sx={{
              borderRadius: 2,
            }}
          >
            {closedAllDay
              ? isFrench
                ? "Cette journée sera complètement fermée. Les heures seront enregistrées de 00:00 à 12:00."
                : "This day will be completely closed. The hours will be saved as 12:00 AM to 12:00 PM."
              : isFrench
                ? "L'heure d'ouverture doit être antérieure à l'heure de fermeture."
                : "The opening time must be earlier than the closing time."}
          </Alert>
        </Stack>
      </DialogContent>

      <Divider />

      {/* ======================================================
          ACTIONS
      ====================================================== */}

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {isFrench ? "Annuler" : "Cancel"}
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading
            ? isFrench
              ? "Enregistrement..."
              : "Saving..."
            : isEdit
              ? isFrench
                ? "Enregistrer"
                : "Save Changes"
              : isFrench
                ? "Ajouter"
                : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialBusinessHoursDialog;
